import React from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp } from 'lucide-react';

// 1. We pass 't' to the tooltip so it can translate the labels
function ChartTooltipContent({ active, payload, label, t }) {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-lg border bg-background p-2 shadow-sm">
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col">
            <span className="text-[0.70rem] uppercase text-muted-foreground">
              {/* 2. Use specific keys from your JSON files */}
              {payload[0].payload.period === 'day' ? t('time') : t('date')}
            </span>
            <span className="font-bold text-muted-foreground">{label}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-[0.70rem] uppercase text-muted-foreground">Rate</span>
            <span className="font-bold">{payload[0].value.toFixed(1)}%</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
}

export function RecyclingChart({ data }) {
  // 3. Destructure i18n to get the current language (en or nl)
  const { t, i18n } = useTranslation();

  const chartData = data?.historical_data?.map(item => ({
    date: item.date,
    recyclingRate: item.rate,
    period: data.period 
  })) || [];

  const currentRate = data?.recycling_rate || 0;
  const percentage = currentRate.toFixed(1);
  const period = data?.period || 'year';

  const getColorScheme = (rate) => {
    if (rate >= 70) return { stroke: '#10b981', fill: '#10b981', fillOpacity: 0.2, text: 'text-green-600' };
    if (rate >= 50) return { stroke: '#3b82f6', fill: '#3b82f6', fillOpacity: 0.2, text: 'text-blue-500' };
    return { stroke: '#ef4444', fill: '#ef4444', fillOpacity: 0.2, text: 'text-red-500' };
  };

  const colorScheme = getColorScheme(currentRate);

  return (
    <div className="rounded-lg border bg-card text-card-foreground dark:hover:bg-slate-900 hover:cursor-pointer flex flex-col w-full h-[200px]">
      <div className="px-4 py-3 flex items-center justify-between">
        {/* This key exists in your JSON: "defaultTitle": "Recyling Rate" / "Recyclingpercentage" */}
        <h2 className="tracking-tight text-sm font-medium pb-2"> {t('recyclingRate.defaultTitle')} </h2>
      </div>

      <div className={`px-4 pb-1 text-2xl font-bold ${colorScheme.text}`}>
        {percentage}%
      </div>

      <div className="flex-1 w-full px-0 pt-0 overflow-hidden rounded-b-lg">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 10, bottom: 20 }}
          >
            <XAxis
              dataKey="date"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: '#9CA3AF' }}
              dy={10}
              interval="preserveStartEnd"
              tickFormatter={(value) => {
                // Handle 'Day' view (Time)
                if (period === 'day') {
                   // Uses i18n.language to format time (e.g., 2:00 PM vs 14:00)
                   return new Date(value).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });
                }
                
                // Handle 'Week' view (assuming value is raw string)
                if (period === 'week') {
                   return value; 
                }
                
                // Handle Month/Year views
                try {
                   const date = new Date(value);
                   if(isNaN(date.getTime())) return value.split(' ')[0];

                   // FIX: Pass i18n.language as the first argument.
                   // This ensures it uses 'en' or 'nl' based on your active JSON file.
                   return date.toLocaleDateString(i18n.language, { month: 'short' });
                } catch(e) {
                   return value;
                }
              }}
            />
            <YAxis hide domain={[0, 100]} />
            {/* Pass the translation function 't' to the tooltip */}
            <Tooltip 
                content={<ChartTooltipContent t={t} />} 
                wrapperStyle={{ outline: 'none' }} 
            />
            <Area
              type="monotone"
              dataKey="recyclingRate"
              stroke={colorScheme.stroke}
              strokeWidth={3}
              fill={colorScheme.fill}
              fillOpacity={colorScheme.fillOpacity}
              strokeLinecap="round"
              strokeLinejoin="round"
              radius={[10, 10, 0, 0]}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}